'use client'

import * as React from 'react'
import { tw } from 'utils/classnames'
import { Divider } from 'components/divider'
import { useWindowDomEvent } from 'hooks/use-dom-event'
import { motion } from 'framer-motion'
import { usePathname } from '@repo/nextjs-utils/navigation'
import { AppRoutes } from '@repo/web-utils/app-routes'
import type { Language } from 'codegen/types'
import { HeaderContextProvider } from 'cf-menu/contexts/header-config-context'
import { useLocale, useTranslations } from 'next-intl'
import { useLanguageAndCurrency } from '@repo/web-ui/hooks/use-language-and-currency'

import type { StudioHeaderContainerProps } from './'

enum Variant {
  DARK = 'DARK',
  LIGHT = 'LIGHT',
}

const animationVariants = {
  show: {
    y: 0,
    transition: { duration: 0.3, ease: 'easeInOut' as const },
  },
  hide: {
    y: '-100%',
    transition: { duration: 0.3, ease: 'easeInOut' as const },
  },
}

export function StudioHeaderContainer({
  children,
  classNames,
}: Required<StudioHeaderContainerProps>) {
  const pathname = usePathname()
  const changingVariant = [
    AppRoutes.STUDIO,
    AppRoutes.TOOLS_FONT_GENERATOR,
    AppRoutes.TOOLS_AI_FONT_GENERATOR,
  ].includes(pathname)
  const [showHeader, setShowHeader] = React.useState(true)
  const [variant, setVariant] = React.useState<Variant>(
    changingVariant ? Variant.DARK : Variant.LIGHT,
  )
  const lastScrollY = React.useRef(0)

  const isLight = variant === Variant.LIGHT

  const handleVariantChange = () => {
    // went with this approach to be able to keep the parent component as a server component
    const elementToWatchForChangingVariant = document.querySelector('#change_header_color')

    if (elementToWatchForChangingVariant) {
      const elementPosition = elementToWatchForChangingVariant.getBoundingClientRect()
      if (elementPosition.top <= 0) {
        setVariant(Variant.LIGHT)
      } else {
        setVariant(Variant.DARK)
      }
    }
  }

  const handleScroll = React.useCallback(() => {
    // oxlint-disable-next-line no-negated-condition
    const currentScrollY = typeof window !== 'undefined' ? window.scrollY : 0

    // oxlint-disable-next-line no-negated-condition
    if (!changingVariant) {
      if (currentScrollY < lastScrollY.current) {
        setShowHeader(true)
      } else if (currentScrollY > lastScrollY.current && currentScrollY > 64) {
        setShowHeader(false)
      }
    } else {
      handleVariantChange()
    }

    lastScrollY.current = currentScrollY
  }, [changingVariant])

  useWindowDomEvent('scroll', handleScroll, { passive: true })
  React.useEffect(handleScroll, [handleScroll])

  const locale = useLocale()
  const t = useTranslations('cf_menu')
  const { activeCurrency, onChangeCurrency, onChangeLanguage } = useLanguageAndCurrency()

  return (
    <motion.div
      initial="show"
      animate={showHeader ? 'show' : 'hide'}
      variants={animationVariants}
      style={{
        '--navigation-text-color': isLight ? 'var(--100)' : 'var(--800)',
        '--navigation-background-color': isLight ? 'var(--white-white)' : '#121317',
        '--navigation-search-text-input-color': isLight ? 'var(--500)' : 'var(--700)',
        '--navigation-search-icon-color': isLight ? 'var(--500)' : 'var(--400-400)',
        '--navigation-search-border-color': isLight ? 'var(--700)' : 'var(--100)',
        '--navigation-search-result-hover-background-color': isLight ? 'var(--800)' : 'var(--100)',
        '--navigation-border-color': isLight ? 'var(--800)' : 'rgba(39,43,47,0.5)',
        '--navigation-menu-text-color': isLight ? 'var(--50)' : 'var(--white-white)',
        '--navigation-menu-hover-background-color': isLight ? 'var(--900)' : 'var(--white-white)',
        '--navigation-menu-hamburger-icon-color': isLight ? 'var(--50)' : 'var(--white-white)',
      }}
      className={tw(
        classNames,
        'sticky top-0 z-30 flex flex-col bg-[--navigation-background-color] pt-0',
        variant === Variant.DARK && 'pb-0',
        isLight && 'border-b border-gray-800 shadow-[0px_10px_10px_0px_rgba(0,_0,_0,_0.04)]',
      )}
    >
      <HeaderContextProvider
        activeCurrency={activeCurrency}
        lang={locale as Language}
        i18n={t}
        onLanguageChange={onChangeLanguage}
        onCurrencyChange={onChangeCurrency}
      >
        <div className="flex flex-col">
          {children}
          {variant === Variant.DARK && changingVariant ? (
            <Divider
              className={`h-[1px] bg-gray-100 bg-opacity-50 bg-[linear-gradient(to_right,rgba(39,43,47,0.5)_40%,#865CF9_50%,rgba(39,43,47,0.5)_60%)]`}
            />
          ) : null}
        </div>
      </HeaderContextProvider>
    </motion.div>
  )
}
